import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import {
  getCustomAttributesForUser,
  getAttributeColumns,
} from '../../services/internal/user-settings-service'

export const fetchCustomAttributesAction = createAsyncThunk(
  'settings/fetchCustomAttributes',
  async (data: { showArchived; userId; attributeName }) => {
    const { showArchived, userId, attributeName } = data
    return await getCustomAttributesForUser({
      showArchived,
      userId,
      attributeName,
    })
  },
)

export const fetchNumericAttributesAction = createAsyncThunk(
  'settings/fetchNumericAttributes',
  async () => {
    return await getAttributeColumns({ numericOnly: true })
  },
)

export const fetchDeletedCustomAttributesAction = createAsyncThunk(
  'settings/fetchDeletedCustomAttributes',
  async (userId?: string) => {
    return await getCustomAttributesForUser({ showArchived: true, userId })
  },
)

const initialState = {
  customAttributeList: [] as any[],
  isFetchingCustAttrs: false,
  isFetchingNumericColumns: false,
  numericColumnList: [] as any[],
  isFetchingDeletedCustomAttributes: false,
  deletedCustomAttributeList: [] as any[],
}

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    resetCustomAttributesAction: state => {
      state.isFetchingCustAttrs = false
      state.customAttributeList = []
    },
  },
  extraReducers: builder => {
    builder

      // fetchCustomAttributesAction
      .addCase(fetchCustomAttributesAction.pending, state => {
        state.isFetchingCustAttrs = true
      })
      .addCase(fetchCustomAttributesAction.fulfilled, (state, action) => {
        const attributes = action.payload
          ?.filter(attribute => attribute.id === 'custom_attributes')
          .flatMap(attributeObject =>
            Object.entries(attributeObject.attributes)?.map(
              ([key, value]: [any, any]) => ({
                ...value,
                id: key,
              }),
            ),
          )
        state.isFetchingCustAttrs = true
        state.customAttributeList = attributes
      })
      .addCase(fetchCustomAttributesAction.rejected, state => {
        state.isFetchingCustAttrs = false
        state.customAttributeList = []
      })

      // fetchNumericAttributesAction
      .addCase(fetchNumericAttributesAction.pending, state => {
        state.isFetchingNumericColumns = true
      })
      .addCase(fetchNumericAttributesAction.fulfilled, (state, action) => {
        const attributes = action.payload?.flatMap(attributeObject =>
          Object.entries(attributeObject.attributes).reduce(
            (attr: any, [key, value]: [any, any]) => {
              attr.push({
                name: value.name,
                id: `${attributeObject.id}.${key}`,
              })
              return attr
            },
            [],
          ),
        )
        state.isFetchingNumericColumns = false
        state.numericColumnList = attributes
      })
      .addCase(fetchNumericAttributesAction.rejected, state => {
        state.isFetchingNumericColumns = false
        state.numericColumnList = []
      })

      // fetchDeletedCustomAttributesAction
      .addCase(fetchDeletedCustomAttributesAction.pending, state => {
        state.isFetchingDeletedCustomAttributes = true
      })
      .addCase(
        fetchDeletedCustomAttributesAction.fulfilled,
        (state, action) => {
          const userId = action.meta.arg
          const attributes = action.payload
            ?.filter(attribute => attribute.id === 'custom_attributes')
            .flatMap(attributeObject =>
              Object.entries(attributeObject.attributes).reduce(
                (acc: any, [key, value]: [any, any]) => {
                  if (value.archived_users.includes(userId)) {
                    acc.push({
                      ...value,
                      id: key,
                    })
                  }
                  return acc
                },
                [],
              ),
            )
          state.isFetchingDeletedCustomAttributes = false
          state.deletedCustomAttributeList = attributes ?? []
        },
      )
      .addCase(fetchDeletedCustomAttributesAction.rejected, state => {
        state.isFetchingDeletedCustomAttributes = false
        state.deletedCustomAttributeList = []
      })
  },
})

export const { resetCustomAttributesAction } = settingsSlice.actions

export default settingsSlice.reducer
